import { flow, types, cast, getSnapshot } from 'mobx-state-tree';
import {
  getAudiobookDetails,
  triggerTocExtraction,
  saveTocAndCreateChapters,
  listChapters,
  getChapterContent,
  putChapterContent,
  processChapter,
  approveChapter,
  generateChapterAudio,
  generateAudioBatch,
  processAll,
  processFull,
  listAudioUrls,
  listRuns,
  getRun,
  cancelRun,
  cancelAllRuns,
  resetChapterAudio,
  listAudiobooks,
  listAudiobooksLight,
  setAudiobookStatus,
  deleteAudiobook,
  linkCsAudiobookToPublic,
} from 'services/csAudiobookApi';
import { withRootStore } from '../withRootStore';
import type { CsAudiobookDetailsApi, CsApiTocItem } from 'types/CsAudiobook';

const CsTocChapterModel = types.model('CsTocChapter', {
  title: types.string,
  ebookPage: types.maybeNull(types.number),
  pdfPage: types.maybeNull(types.number),
  originalChapterId: types.maybeNull(types.string),
});

export const CsAudiobookDetailsModel = types.model('CsAudiobookDetails', {
  id: types.identifier,
  title: types.maybeNull(types.string),
  author: types.maybeNull(types.string),
  language: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  pageOffset: types.maybeNull(types.number),
  toc: types.optional(types.array(CsTocChapterModel), []),
  publicAudiobookId: types.maybeNull(types.string),
});

export const CsAudiobookStore = types
  .model('CsAudiobookStore', {
    items: types.optional(types.array(types.frozen()), []),
    detailsById: types.map(CsAudiobookDetailsModel),
    chaptersById: types.map(types.frozen()),
    loadingByKey: types.map(types.boolean),
    lastFetchedAt: types.maybeNull(types.number),
    // UI summary for audio generation runs per audiobook id
    audioRunUiById: types.optional(types.map(types.frozen()), {}),
    // Track current audio_generation runId per audiobook to fetch detailed progress
    audioRunIdByBook: types.optional(types.map(types.string), {}),
  })
  .extend(withRootStore)
  .views((self) => ({
    get itemsArray() {
      // Ensure a tracked read for reactivity
      return self.items.slice();
    },
  }))
  .actions((self) => {
    const setLoading = (key: string, val: boolean) => {
      self.loadingByKey.set(key, val);
    };
    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const CACHE_KEY = 'cs_audiobooks_cache';

    // One poller per audiobook id (in-memory, not persisted)
    type AudioPoller = {
      timer: number | null;
      isPolling: boolean;
      inFlight: boolean;
      backoffMs: number;
      failures: number;
      requestSeq: number;
      stopping: boolean;
      awaitingFreshRun: boolean;
    };
    type AudioRunChapter = {
      processedUnits?: number;
      totalUnits?: number;
      status?: string;
      stage?: string;
      lastMessage?: string;
    };
    const aggregateChapterUnits = (chapters?: AudioRunChapter[]) => {
      let processedUnits = 0;
      let totalUnits = 0;
      if (Array.isArray(chapters)) {
        for (const c of chapters) {
          const rawPu = Number(c.processedUnits ?? 0);
          const rawTu = Number(c.totalUnits ?? 0);
          const stage = String(c.stage ?? '');
          const lastMsg = String(c.lastMessage ?? '');
          let displayProcessed = /sending|streaming/i.test(stage) ? Math.max(rawPu - 1, 0) : Math.max(rawPu, 0);
          let displayTotal = Number.isFinite(rawTu) ? Math.max(rawTu, 0) : 0;
          if (displayTotal === 0 && lastMsg) {
            const m =
              lastMsg.match(/Generated\s+chunk\s*(\d+)\s*\/\s*(\d+)/i) ||
              lastMsg.match(/Sending\s+chunk\s*(\d+)\s*\/\s*(\d+)/i) ||
              lastMsg.match(/Streaming\s+chunk\s*(\d+)\s*\/\s*(\d+)/i);
            if (m) {
              const idx = Math.max(1, Number(m[1] || 1));
              const tot = Math.max(0, Number(m[2] || 0));
              const isGenerated = /Generated/i.test(m[0] || '');
              const derivedProcessed = isGenerated ? idx : Math.max(idx - 1, 0);
              if (Number.isFinite(tot) && tot > 0) {
                displayTotal = tot;
                displayProcessed = Math.min(derivedProcessed, tot);
              }
            }
          }
          if (Number.isFinite(displayProcessed)) processedUnits += displayProcessed;
          if (Number.isFinite(displayTotal)) totalUnits += displayTotal;
        }
      }
      return { processedUnits, totalUnits };
    };
    const isActiveRunStatus = (status?: string | null) => {
      if (!status) return false;
      const normalized = String(status).toLowerCase();
      return normalized === 'running' || normalized === 'queued' || normalized === 'starting';
    };
    type AudioUiState = {
      completed: number;
      total: number;
      message?: string;
      cancelling?: boolean;
      running?: boolean;
      processedUnits?: number;
      totalUnits?: number;
    };
    type SelfWithUiHelpers = typeof self & {
      setAudioRunUi: (id: string, ui: AudioUiState | null) => void;
      updatePinnedRunId: (bookId: string, runId?: string | null) => void;
    };
    const selfWithUi = self as SelfWithUiHelpers;

    const audioPollers: Map<string, AudioPoller> = new Map();
    const getPoller = (id: string): AudioPoller => {
      const existing = audioPollers.get(id);
      if (existing) return existing;
      const created: AudioPoller = {
        timer: null,
        isPolling: false,
        inFlight: false,
        backoffMs: 1500,
        failures: 0,
        requestSeq: 0,
        stopping: false,
        awaitingFreshRun: false,
      };
      audioPollers.set(id, created);
      return created;
    };
    const clearPollerTimer = (id: string) => {
      const p = audioPollers.get(id);
      if (p?.timer) {
        window.clearTimeout(p.timer);
        p.timer = null;
      }
    };
    const scheduleNextAudioPoll = (id: string) => {
      const p = getPoller(id);
      const visible = typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;
      // 1–2s when visible; 5s when hidden
      const base = visible ? 1500 : 5000;
      const jitter = visible ? Math.floor(Math.random() * 500) : Math.floor(Math.random() * 1000);
      const delay = Math.max(base + jitter, p.backoffMs);
      clearPollerTimer(id);
      p.timer = window.setTimeout(() => void pollAudioRunsOnce(id), delay);
    };
    const localizeAudioMessage = (raw?: string) => {
      if (!raw) return '';
      const msg = raw.trim().replace(/^\s*0+(?=[A-Za-z])/i, '');
      if (!msg) return '';
      const lowered = msg.toLowerCase();
      if (
        lowered === 'starting audio generation' ||
        lowered === 'starting audio generation...' ||
        lowered.startsWith('starting audio generation')
      ) {
        return 'Aloitetaan äänen luonti…';
      }
      if (lowered.startsWith('queued audio job') || lowered.startsWith('queueing audio job')) {
        return 'Ääntä jonotetaan…';
      }
      if (lowered.startsWith('audio generation cancelled') || lowered.startsWith('cancelled audio generation')) {
        return 'Äänen luonti peruutettu';
      }
      if (lowered.startsWith('sending chunk')) {
        return msg.replace(/^sending chunk/i, 'Lähetetään osa').replace(/chunk/i, 'osa');
      }
      if (lowered.startsWith('streaming chunk')) {
        return msg.replace(/^streaming chunk/i, 'Suoratoistetaan osa').replace(/chunk/i, 'osa');
      }
      if (lowered.startsWith('generated chunk')) {
        return msg.replace(/^generated chunk/i, 'Luotiin osa').replace(/chunk/i, 'osa');
      }
      return msg;
    };

    const pollAudioRunsOnce = async (id: string) => {
      const p = getPoller(id);
      if (!p.isPolling || p.stopping) {
        clearPollerTimer(id);
        return;
      }
      if (p.inFlight) {
        scheduleNextAudioPoll(id);
        return;
      }
      p.inFlight = true;
      const seq = ++p.requestSeq;
      try {
        const res = (await listRuns(id, { type: 'audio_generation' })) as { data?: unknown } | null;
        const payload = (res as unknown as { data?: unknown })?.data;
        const arr = Array.isArray(payload)
          ? (payload as unknown[])
          : payload && typeof payload === 'object'
            ? ((payload as { items?: unknown[] })?.items as unknown[])
            : [];
        const runs = Array.isArray(arr)
          ? (arr as Array<{
              id?: unknown;
              status?: unknown;
              totalChapters?: unknown;
              completedChapters?: unknown;
              failedChapters?: unknown;
              lastMessage?: unknown;
              startedAt?: unknown;
            }>)
          : [];
        // Sort by startedAt desc if available
        runs.sort((a, b) => {
          const as = Date.parse(String((a as { startedAt?: unknown }).startedAt ?? '') || '');
          const bs = Date.parse(String((b as { startedAt?: unknown }).startedAt ?? '') || '');
          return (isNaN(bs) ? 0 : bs) - (isNaN(as) ? 0 : as);
        });
        const pinnedRunIdRaw = self.audioRunIdByBook.get(id);
        const pinnedRunId = pinnedRunIdRaw ? String(pinnedRunIdRaw) : null;
        const runningRuns = runs.filter((r) => isActiveRunStatus(String((r as { status?: unknown }).status ?? '')));
        const firstRunId =
          runningRuns.length > 0
            ? String((runningRuns[0] as { id?: unknown }).id ?? '')
            : runs[0]
              ? String((runs[0] as { id?: unknown }).id ?? '')
              : null;
        if (!pinnedRunId && firstRunId) {
          selfWithUi.updatePinnedRunId(id, firstRunId);
        }
        const trackingRunId = pinnedRunId ?? firstRunId ?? null;

        const detailIds: string[] = [];
        const addDetailId = (runId: string | null) => {
          if (!runId) return;
          if (!detailIds.includes(runId)) {
            detailIds.push(runId);
          }
        };
        for (const run of runningRuns) {
          addDetailId(String((run as { id?: unknown }).id ?? ''));
        }
        addDetailId(trackingRunId);

        const detailMap = new Map<
          string,
          {
            id: string;
            status?: string;
            totalChapters?: number;
            completedChapters?: number;
            failedChapters?: number;
            lastMessage?: string;
            chapters?: AudioRunChapter[];
          }
        >();
        for (const detailId of detailIds) {
          try {
            const resRun = (await getRun(id, detailId)) as {
              data?: {
                id: string;
                status?: string;
                totalChapters?: number;
                completedChapters?: number;
                failedChapters?: number;
                lastMessage?: string;
                chapters?: AudioRunChapter[];
              };
            } | null;
            if (resRun?.data) {
              detailMap.set(detailId, resRun.data);
            }
          } catch (_) {
            // ignore detail fetch errors
          }
        }

        let aggregatedCompleted = 0;
        let aggregatedTotal = 0;
        let aggregatedProcessedUnits = 0;
        let aggregatedTotalUnits = 0;
        let displayMessage: string | undefined;
        let anyActive = false;

        const pickMessage = (raw?: string, prefer = false) => {
          if (!raw) return;
          const localized = localizeAudioMessage(raw);
          if (!localized) return;
          if (!displayMessage || prefer) {
            displayMessage = localized;
          }
        };

        const includeDetail = (
          detail:
            | {
                id: string;
                status?: string;
                totalChapters?: number;
                completedChapters?: number;
                failedChapters?: number;
                lastMessage?: string;
                chapters?: AudioRunChapter[];
              }
            | undefined,
          preferMessage = false
        ) => {
          if (!detail) return;
          const status = detail.status ? String(detail.status).toLowerCase() : '';
          const active = isActiveRunStatus(status);
          if (active) {
            anyActive = true;
          }
          if (active || preferMessage || p.awaitingFreshRun) {
            aggregatedCompleted += Number(detail.completedChapters ?? 0) + Number(detail.failedChapters ?? 0);
            aggregatedTotal += Number(detail.totalChapters ?? detail.chapters?.length ?? 0);
            const units = aggregateChapterUnits(detail.chapters);
            aggregatedProcessedUnits += units.processedUnits;
            aggregatedTotalUnits += units.totalUnits;
          }
          pickMessage(detail.lastMessage, preferMessage);
          if (!active && preferMessage && !p.awaitingFreshRun) {
            selfWithUi.updatePinnedRunId(id, null);
          }
        };

        includeDetail(trackingRunId ? detailMap.get(trackingRunId) : undefined, true);
        detailMap.forEach((detail, detailId) => {
          if (detailId === trackingRunId) return;
          includeDetail(detail, false);
        });

        for (const run of runningRuns) {
          const runId = String((run as { id?: unknown }).id ?? '');
          if (detailMap.has(runId)) continue;
          const status = String((run as { status?: unknown }).status ?? '');
          if (isActiveRunStatus(status)) {
            anyActive = true;
          }
          aggregatedCompleted +=
            Number((run as { completedChapters?: unknown }).completedChapters ?? 0) +
            Number((run as { failedChapters?: unknown }).failedChapters ?? 0);
          aggregatedTotal += Number((run as { totalChapters?: unknown }).totalChapters ?? 0);
          pickMessage(String((run as { lastMessage?: unknown }).lastMessage ?? ''), false);
        }

        if (p.awaitingFreshRun && anyActive) {
          p.awaitingFreshRun = false;
        }

        if (seq === p.requestSeq && (anyActive || !p.awaitingFreshRun)) {
          const ui = {
            completed: Math.max(aggregatedCompleted, 0),
            total: Math.max(aggregatedTotal || runningRuns.length || 1, 0),
            message: displayMessage || (runningRuns.length > 1 ? 'Useita ääniä luodaan…' : 'Ääntä luodaan…'),
            cancelling: (self.audioRunUiById.get(id) as { cancelling?: boolean } | undefined)?.cancelling ?? false,
            running: anyActive || p.awaitingFreshRun,
            processedUnits: aggregatedProcessedUnits > 0 ? aggregatedProcessedUnits : undefined,
            totalUnits: aggregatedTotalUnits > 0 ? aggregatedTotalUnits : undefined,
          };
          selfWithUi.setAudioRunUi(id, ui);
        }

        // Reset backoff on success
        p.backoffMs = 1500;
        p.failures = 0;

        const shouldStop = !p.awaitingFreshRun && !anyActive;
        if (shouldStop) {
          p.isPolling = false;
          p.stopping = true;
          clearPollerTimer(id);
          selfWithUi.setAudioRunUi(id, null);
          p.awaitingFreshRun = false;
          selfWithUi.updatePinnedRunId(id, null);
          p.stopping = false;
          return;
        }
      } catch (err: unknown) {
        p.failures += 1;
        p.backoffMs = Math.min(p.backoffMs * 2, 8000);
        if (p.failures >= 3) {
          // Surface error once and stop to avoid hammering
          const message = (err as Error)?.message ?? 'api_error';
          self.rootStore.errorStore.setError(message);
          p.isPolling = false;
          clearPollerTimer(id);
          return;
        }
      } finally {
        p.inFlight = false;
      }
      scheduleNextAudioPoll(id);
    };
    const startAudioRunsPollingAction = flow(function* (id: string) {
      const p = getPoller(id);
      if (p.isPolling) return;
      p.isPolling = true;
      p.failures = 0;
      p.backoffMs = 1500;
      p.requestSeq = 0;
      p.stopping = false;
      // eslint-disable-next-line no-console
      console.debug('[AudioPoll] start', { audiobookId: id });
      // Kick an immediate poll and then schedule
      try {
        yield pollAudioRunsOnce(id);
      } finally {
        scheduleNextAudioPoll(id);
      }
    });
    const stopAudioRunsPollingAction = (_id: string) => {
      const p = getPoller(_id);
      p.isPolling = false;
      clearPollerTimer(_id);
      // eslint-disable-next-line no-console
      console.debug('[AudioPoll] stop', { audiobookId: _id });
    };
    const primeAudioUiAction = (id: string, message?: string) => {
      selfWithUi.setAudioRunUi(id, {
        completed: 0,
        total: 1,
        message: message ?? 'Aloitetaan äänen luonti…',
        cancelling: false,
        running: true,
      });
      const poller = getPoller(id);
      poller.awaitingFreshRun = true;
      selfWithUi.updatePinnedRunId(id, null);
    };

    const hydrateFromCache = () => {
      try {
        if (self.items.length > 0) return;
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (!raw) return;
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr) || arr.length === 0) return;
        self.items = cast(arr);
      } catch (_) {
        // ignore cache errors
      }
    };

    const mapDetails = (id: string, data: CsAudiobookDetailsApi) => {
      const toc = (data.toc ?? []).map((c: CsApiTocItem) => ({
        title: c.title,
        // Auto-detected page numbers should populate the Ebook field
        ebookPage: c.ebook_page ?? c.page_number ?? c.start_page_idx ?? null,
        // PDF page must be set by user separately (only accept explicit pdf_page)
        pdfPage: c.pdf_page ?? null,
        originalChapterId: c.original_chapter_id ?? null,
      }));
      const publicAudiobookIdRaw = data.publicAudiobookId ?? data.public_audiobook_id ?? null;
      const publicAudiobookId =
        publicAudiobookIdRaw !== undefined && publicAudiobookIdRaw !== null ? String(publicAudiobookIdRaw) : null;

      self.detailsById.set(
        id,
        CsAudiobookDetailsModel.create({
          id,
          title: data.title ?? null,
          author: data.author ?? null,
          language: data.language ?? null,
          status: data.status ?? null,
          pageOffset: data.page_offset ?? null,
          toc,
          publicAudiobookId,
        })
      );
    };

    const getDetailsAction = flow(function* (id: string) {
      setLoading(`details:${id}`, true);
      try {
        const res = yield getAudiobookDetails(id);
        const data = res.data ?? {};
        mapDetails(id, data);
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
      } finally {
        setLoading(`details:${id}`, false);
      }
    });

    const getDetailsSilentAction = flow(function* (id: string) {
      try {
        const res = yield getAudiobookDetails(id);
        const data = res.data ?? {};
        mapDetails(id, data);
      } catch (err) {
        // silent
      }
    });

    const listAllAction = flow(function* (opts?: {
      force?: boolean;
      limit?: number;
      startAfter?: string;
      organizationId?: string;
      light?: boolean;
      background?: boolean;
    }) {
      // Try session cache for instant paint
      hydrateFromCache();
      const now = Date.now();
      const isFresh = self.lastFetchedAt && now - (self.lastFetchedAt as number) < 60_000; // 60s cache
      const shouldUseCache = !opts?.force && isFresh && self.items.length > 0;
      if (shouldUseCache) {
        return getSnapshot(self.items);
      }

      const shouldShowLoading = !opts?.background;
      if (shouldShowLoading) setLoading('list', true);
      try {
        const apiCall = opts?.light ? listAudiobooksLight : listAudiobooks;
        const params = {
          limit: opts?.limit ?? 50,
          start_after: opts?.startAfter,
          organization_id: opts?.organizationId,
        } as const;

        let res = yield apiCall(params);
        const extractArray = (payload: unknown, depth = 0): unknown[] => {
          if (Array.isArray(payload)) return payload;
          if (!payload || typeof payload !== 'object' || depth > 2) return [];
          const obj = payload as Record<string, unknown>;
          // Direct known keys
          const directKeys = ['items', 'results', 'rows', 'docs', 'data'];
          for (const key of directKeys) {
            const val = obj[key];
            if (Array.isArray(val)) return val as unknown[];
            // Some APIs return maps keyed by id under these keys
            if (val && typeof val === 'object' && !Array.isArray(val)) {
              const mapObj = val as Record<string, unknown>;
              const values = Object.values(mapObj);
              if (values.length > 0 && values.every((v) => v && typeof v === 'object')) {
                return values as unknown[];
              }
            }
          }
          // One level deeper for common nestings (e.g., data.items)
          for (const key of directKeys) {
            const val = obj[key];
            const nested = extractArray(val, depth + 1);
            if (nested.length > 0) return nested;
          }
          // Fallback: first array property found
          for (const key of Object.keys(obj)) {
            const val = obj[key];
            if (Array.isArray(val)) return val as unknown[];
          }
          // Fallback: treat plain object with many object-like children as a map
          const values = Object.values(obj);
          if (values.length > 0 && values.every((v) => v && typeof v === 'object')) {
            return values as unknown[];
          }
          return [];
        };
        let incoming = extractArray(res.data);

        // If first fetch returns empty, retry once after a short delay (eventual consistency)
        const isFirstFetch = !self.lastFetchedAt;
        if (isFirstFetch && Array.isArray(incoming) && incoming.length === 0) {
          yield sleep(800);
          res = yield apiCall(params);
          incoming = extractArray(res.data);
        }

        const isEmpty = Array.isArray(incoming) && incoming.length === 0;
        // If we already have items and server responds empty, keep existing to avoid flicker
        if (isEmpty && self.items.length > 0) {
          self.lastFetchedAt = Date.now();
          return getSnapshot(self.items);
        }

        // If first load and still empty, do not mark fetched to avoid premature "No audiobooks"
        if (isEmpty && self.items.length === 0) {
          return [];
        }

        // Update items normally
        self.items = cast(incoming as unknown[]);
        self.lastFetchedAt = Date.now();
        // Persist lightweight cache for fast hydration next time
        try {
          const toCache = Array.isArray(incoming) ? (incoming as unknown[]).slice(0, 100) : [];
          sessionStorage.setItem(CACHE_KEY, JSON.stringify(toCache));
        } catch (_) {
          // ignore cache errors
        }
        return incoming;
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status !== 404) {
          const message = (err as Error)?.message ?? 'api_error';
          self.rootStore.errorStore.setError(message);
        }
        // For 404, keep existing items and continue showing loading states elsewhere until a successful fetch
        return getSnapshot(self.items);
      } finally {
        if (shouldShowLoading) setLoading('list', false);
      }
    });

    const triggerToc = flow(function* (id: string) {
      setLoading(`extract_toc:${id}`, true);
      try {
        const res = yield triggerTocExtraction(id);
        self.rootStore.errorStore.setSuccess('save_success');
        return res?.data;
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
        throw err;
      } finally {
        setLoading(`extract_toc:${id}`, false);
      }
    });

    const autoDetectTocAction = flow(function* (id: string) {
      setLoading(`extract_toc:${id}`, true);
      try {
        // Start extraction
        const start = yield triggerTocExtraction(id);
        const runId = start?.data?.runId as string | undefined;
        // Poll for results and update details in-place; break immediately once TOC is present
        const deadline = Date.now() + 120000; // max 2 minutes
        while (Date.now() < deadline) {
          yield getDetailsSilentAction(id);
          const current = self.detailsById.get(id);
          if (current && (current.toc?.length ?? 0) > 0) {
            break;
          }
          yield sleep(2000);
        }
        // Final visible refresh
        yield getDetailsAction(id);
        return { runId };
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
        throw err;
      } finally {
        setLoading(`extract_toc:${id}`, false);
      }
    });

    const saveToc = flow(function* (
      id: string,
      payload: { page_offset: number; chapters: Array<Record<string, unknown>> }
    ) {
      try {
        const res = yield saveTocAndCreateChapters(id, payload);
        self.rootStore.errorStore.setSuccess('save_success');
        return res?.data;
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
        throw err;
      }
    });

    const listChaptersAction = flow(function* (id: string) {
      setLoading(`chapters:${id}`, true);
      try {
        const res = yield listChapters(id);
        self.chaptersById.set(id, res.data ?? []);
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
      } finally {
        setLoading(`chapters:${id}`, false);
      }
    });

    const getChapterContentAction = flow(function* (id: string, chapterId: string) {
      try {
        const res = yield getChapterContent(id, chapterId);
        return res.data?.content ?? '';
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
        return '';
      }
    });

    const updateChapterContentAction = flow(function* (
      id: string,
      chapterId: string,
      content: string,
      opts?: { updateRaw?: boolean }
    ) {
      try {
        yield putChapterContent(id, chapterId, { content, update_raw: opts?.updateRaw });
        self.rootStore.errorStore.setSuccess('save_success');
        // Refresh chapters cache
        yield listChaptersAction(id);
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
      }
    });

    const processChapterAction = flow(function* (id: string, chapterId: string) {
      try {
        const res = yield processChapter(id, chapterId);
        return res?.data;
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
        return null;
      }
    });

    const approveChapterAction = flow(function* (id: string, chapterId: string, voice_id?: string) {
      try {
        yield approveChapter(id, chapterId, voice_id);
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
      }
    });

    const generateChapterAudioAction = flow(function* (
      id: string,
      chapterId: string,
      body: { useOpenai?: boolean; voiceId?: string; stability?: number; similarityBoost?: number }
    ) {
      try {
        // Prime UI and start polling immediately for responsive UX
        selfWithUi.setAudioRunUi(id, {
          completed: 0,
          total: 1,
          message: 'Aloitetaan äänen luonti…',
          cancelling: false,
          running: true,
        });
        yield startAudioRunsPollingAction(id);
        const res = yield generateChapterAudio(id, chapterId, {
          use_openai: body.useOpenai,
          voice_id: body.voiceId,
          stability: body.stability,
          similarity_boost: body.similarityBoost,
        });
        const data = res?.data;
        if (data?.runId) {
          selfWithUi.updatePinnedRunId(id, String(data.runId));
          // eslint-disable-next-line no-console
          console.debug('[Audio] runId captured', { audiobookId: id, runId: String(data.runId) });
        }
        return data;
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
        throw err;
      }
    });

    const generateAudioBatchAction = flow(function* (
      id: string,
      body: {
        useOpenai?: boolean;
        voiceId?: string;
        stability?: number;
        similarityBoost?: number;
        chapterIds?: string[];
      }
    ) {
      try {
        selfWithUi.setAudioRunUi(id, {
          completed: 0,
          total: 1,
          message: 'Aloitetaan äänierä…',
          cancelling: false,
          running: true,
        });
        yield startAudioRunsPollingAction(id);
        const payload: {
          use_openai?: boolean;
          voice_id?: string;
          stability?: number;
          similarity_boost?: number;
          chapter_ids?: string[];
        } = {
          use_openai: body.useOpenai,
          voice_id: body.voiceId,
          stability: body.stability,
          similarity_boost: body.similarityBoost,
        };
        if (body.chapterIds && body.chapterIds.length > 0) {
          payload.chapter_ids = body.chapterIds;
        }
        const res = yield generateAudioBatch(id, payload);
        const runId = res?.data?.runId ? String(res.data.runId) : 'run_audio_generation_batch';
        if (runId) {
          selfWithUi.updatePinnedRunId(id, runId);
        }
        return res?.data;
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
        throw err;
      }
    });

    const resetChapterAudioAction = flow(function* (
      id: string,
      chapterId: string,
      targetStatus?: 'approved',
      force?: boolean
    ) {
      try {
        const res = yield resetChapterAudio(
          id,
          chapterId,
          targetStatus || force ? { target_status: targetStatus, force } : undefined
        );
        return { ok: true, data: res.data } as { ok: true; data: unknown };
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        const errorKey = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        const message = (errorKey as string) || (err as Error)?.message || 'api_error';
        self.rootStore.errorStore.setError(message);
        return { ok: false, status, error: errorKey } as { ok: false; status?: number; error?: string };
      }
    });

    const processAllAction = flow(function* (id: string) {
      try {
        const res = yield processAll(id);
        return res?.data;
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
        throw err;
      }
    });

    const processFullAction = flow(function* (id: string) {
      try {
        yield processFull(id);
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
      }
    });

    const listAudioUrlsAction = flow(function* (id: string) {
      try {
        const res = yield listAudioUrls(id);
        return res.data;
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
        return null;
      }
    });

    const listRunsAction = flow(function* (
      id: string,
      type?: 'toc_extraction' | 'chapter_creation' | 'text_cleaning' | 'audio_generation'
    ) {
      try {
        const res = yield listRuns(id, type ? { type } : undefined);
        return res?.data;
      } catch (err: unknown) {
        // keep silent to avoid UI noise
        return null;
      }
    });

    const getRunAction = flow(function* (id: string, runId: string) {
      try {
        const res = yield getRun(id, runId);
        return res?.data;
      } catch (err: unknown) {
        return null;
      }
    });

    const cancelRunAction = flow(function* (id: string, runId: string) {
      try {
        yield cancelRun(id, runId);
        return { ok: true } as { ok: true };
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
        return { ok: false } as { ok: false };
      }
    });

    const cancelAllRunsAction = flow(function* (
      id: string,
      type?: 'audio_generation' | 'text_cleaning' | 'chapter_creation' | 'toc_extraction'
    ) {
      try {
        // Set cancelling UI only for audio_generation cancel
        if (!type || type === 'audio_generation') {
          const prev =
            (self.audioRunUiById.get(id) as { completed?: number; total?: number; message?: string } | undefined) ??
            undefined;
          selfWithUi.setAudioRunUi(id, {
            completed: prev?.completed ?? 0,
            total: prev?.total ?? 1,
            message: 'Peruutetaan…',
            cancelling: true,
            running: true,
          });
        }
        // eslint-disable-next-line no-console
        console.debug('[AudioCancelAll] POST', { audiobookId: id });
        yield cancelAllRuns(id, type ? { type } : { type: 'audio_generation' });
        // After successful POST, ensure polling is running to observe stop
        if (!type || type === 'audio_generation') {
          yield startAudioRunsPollingAction(id);
        }
        // eslint-disable-next-line no-console
        console.debug('[AudioCancelAll] OK', { audiobookId: id });
        return { ok: true } as { ok: true };
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
        return { ok: false } as { ok: false };
      }
    });

    const setStatusAction = flow(function* (id: string, status: 'completed' | 'chapters_created') {
      try {
        yield setAudiobookStatus(id, status);
        // Refresh details to reflect status change
        yield getDetailsAction(id);
        self.rootStore.errorStore.setSuccess('save_success');
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
      }
    });

    const linkPublicAudiobookAction = flow(function* (id: string, publicAudiobookId: string) {
      try {
        yield linkCsAudiobookToPublic(id, publicAudiobookId);
        return { ok: true } as { ok: true };
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
        return { ok: false, error: message } as { ok: false; error?: string };
      }
    });

    const removeAction = flow(function* (id: string) {
      setLoading(`delete:${id}`, true);
      try {
        yield deleteAudiobook(id);
        // Remove from items list
        const idx = self.items.findIndex((it) => {
          const raw = it as unknown as { id?: string | number };
          return String(raw?.id ?? '') === String(id);
        });
        if (idx >= 0) self.items.splice(idx, 1);
        // Clear caches
        self.detailsById.delete(id);
        self.chaptersById.delete(id);
        self.rootStore.errorStore.setSuccess('save_success');
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
      } finally {
        setLoading(`delete:${id}`, false);
      }
    });

    return {
      listAll: listAllAction,
      hydrateFromCache,
      setAudioRunUi(
        id: string,
        ui: {
          completed: number;
          total: number;
          message?: string;
          cancelling?: boolean;
          running?: boolean;
          processedUnits?: number;
          totalUnits?: number;
        } | null
      ) {
        if (ui) {
          self.audioRunUiById.set(id, { ...ui });
        } else {
          self.audioRunUiById.delete(id);
        }
      },
      updatePinnedRunId(bookId: string, runId?: string | null) {
        if (runId && runId.length > 0) {
          self.audioRunIdByBook.set(bookId, runId);
        } else {
          self.audioRunIdByBook.delete(bookId);
        }
      },
      getDetails: getDetailsAction,
      getDetailsSilent: getDetailsSilentAction,
      primeAudioUi: primeAudioUiAction,
      startAudioRunsPolling: startAudioRunsPollingAction,
      stopAudioRunsPolling: stopAudioRunsPollingAction,
      triggerToc: triggerToc,
      autoDetectToc: autoDetectTocAction,
      saveToc: saveToc,
      listChapters: listChaptersAction,
      getChapterContent: getChapterContentAction,
      updateChapterContent: updateChapterContentAction,
      processChapter: processChapterAction,
      approveChapter: approveChapterAction,
      generateChapterAudio: generateChapterAudioAction,
      generateAudioBatch: generateAudioBatchAction,
      processAll: processAllAction,
      processFull: processFullAction,
      listAudioUrls: listAudioUrlsAction,
      listRuns: listRunsAction,
      getRun: getRunAction,
      cancelRun: cancelRunAction,
      cancelAllRuns: cancelAllRunsAction,
      resetChapterAudio: resetChapterAudioAction,
      setStatus: setStatusAction,
      linkPublicAudiobook: linkPublicAudiobookAction,
      remove: removeAction,
    };
  });

export type CsAudiobookStoreInstance = typeof CsAudiobookStore.Type;
